import { DEFAULT_TIME_ZONE } from '@/constants';

import data from '@/public/ssr-cache.json';

/**
 * Default Treez store's `dispensary_time_zone` (used on SSR / before the
 * shopper selects a store), then Admin Store TZ (legacy), then the shared
 * constant. Static per deployment, so it's resolved once at module load.
 */
const defaultStoreTimeZone = ((): string => {
  const treezStores = (data?.treezStores ?? []) as {
    short_name?: string;
    dispensary_time_zone?: string;
  }[];
  const defaultStore =
    treezStores.find(
      s => s?.short_name === process.env.NEXT_PUBLIC_TREEZ_DEFAULT_STORE_NAME,
    ) ?? treezStores[0];
  return (
    defaultStore?.dispensary_time_zone ||
    (data?.store as { timezone?: string } | undefined)?.timezone ||
    DEFAULT_TIME_ZONE
  );
})();

/**
 * Single source of truth for the timezone used in store-scoped time logic
 * (discount schedules, dynamic open/closed hours). Fallback order:
 *   1. the passed store's `dispensary_time_zone` (the selected store)
 *   2. the default Treez store's `dispensary_time_zone`
 *   3. Admin / Ecommerce Store `timezone` from SSR cache (legacy)
 *   4. {@link DEFAULT_TIME_ZONE}
 */
export const resolveTimeZone = (dispensaryTimeZone?: string | null): string =>
  dispensaryTimeZone || defaultStoreTimeZone;

const extractTimeZoneName = (
  timeZone: string,
  timeZoneName: 'shortGeneric' | 'short',
): string => {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName })
        .formatToParts(new Date())
        .find(part => part.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
};

export const getTimeZoneAbbreviation = (timeZone: string): string =>
  extractTimeZoneName(timeZone, 'shortGeneric') ||
  extractTimeZoneName(timeZone, 'short');
